import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, AlertCircle, CheckCircle } from 'lucide-react';
import { api } from '../../services/api';
import { Module, CreateTeamDTO, TeamPlan } from '../../types';

export const CreateTeam: React.FC = () => {
  const navigate = useNavigate();
  const [modules, setModules] = useState<Module[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdPassword, setCreatedPassword] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState<Partial<CreateTeamDTO>>({
    name: '',
    slug: '',
    plan: 'starter',
    max_users: 5,
    max_pos: 500,
    billing_email: '',
    allow_super_admin_data_access: false,
    admin_email: '',
    admin_name: '',
    enabled_modules: [],
  });

  // Fetch available modules
  useEffect(() => {
    const fetchModules = async () => {
      try {
        const response = await api.get('/admin/modules');
        setModules(response.data.modules);
        // Pre-select core modules
        const coreModuleIds = response.data.modules
          .filter((m: Module) => m.is_core)
          .map((m: Module) => m.id);
        setFormData(prev => ({ ...prev, enabled_modules: coreModuleIds }));
      } catch (err) {
        console.error('Failed to fetch modules:', err);
        setError('Failed to load modules');
      } finally {
        setIsLoading(false);
      }
    };

    fetchModules();
  }, []);

  // Auto-generate slug from name
  const handleNameChange = (name: string) => {
    const slug = name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .substring(0, 50);
    setFormData(prev => ({ ...prev, name, slug }));
  };

  const handleModuleToggle = (moduleId: string, isCore: boolean) => {
    if (isCore) return; // Can't toggle core modules
    
    setFormData(prev => {
      const current = prev.enabled_modules || [];
      if (current.includes(moduleId)) {
        return { ...prev, enabled_modules: current.filter(id => id !== moduleId) };
      }
      return { ...prev, enabled_modules: [...current, moduleId] };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const response = await api.post('/admin/teams', formData);
      // If password was generated, show it
      if (response.data.adminUser?._generatedPassword) {
        setCreatedPassword(response.data.adminUser._generatedPassword);
      } else {
        navigate(`/admin/teams/${response.data.team.id}`);
      }
    } catch (err: any) {
      console.error('Failed to create team:', err);
      setError(err.response?.data?.error || 'Failed to create team');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPlanLimits = (plan: TeamPlan) => {
    switch (plan) {
      case 'enterprise': return { users: 100, pos: 10000 };
      case 'growth': return { users: 25, pos: 2500 };
      default: return { users: 5, pos: 500 };
    }
  };

  const handlePlanChange = (plan: TeamPlan) => {
    const limits = getPlanLimits(plan);
    setFormData(prev => ({
      ...prev,
      plan,
      max_users: limits.users,
      max_pos: limits.pos,
    }));
  };

  // Group modules by category
  const modulesByCategory = modules.reduce((acc, module) => {
    if (!acc[module.category]) acc[module.category] = [];
    acc[module.category].push(module);
    return acc;
  }, {} as Record<string, Module[]>);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Success screen after creation
  if (createdPassword) {
    return (
      <div className="px-4 py-6 max-w-2xl mx-auto">
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <CheckCircle className="mx-auto h-12 w-12 text-green-600 mb-4" />
          <h2 className="text-xl font-semibold text-green-800 mb-2">Team Created Successfully!</h2>
          <p className="text-green-700 mb-4">
            Save the admin credentials below - the password won't be shown again.
          </p>
          <div className="bg-white rounded-lg p-4 border border-green-200 mb-6 text-left">
            <p className="text-sm text-gray-600 mb-1">Admin Email:</p>
            <p className="font-mono text-lg mb-3">{formData.admin_email}</p>
            <p className="text-sm text-gray-600 mb-1">Generated Password:</p>
            <p className="font-mono text-lg font-bold text-green-700">{createdPassword}</p>
          </div>
          <button
            onClick={() => navigate('/admin/teams')}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Go to Teams
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/admin/teams')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create New Team</h1>
          <p className="text-sm text-gray-500 mt-1">Set up a new team with modules and admin user</p>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700">
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Team Info */}
        <section className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold mb-4">Team Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Team Name *</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => handleNameChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Acme Corp"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Slug *</label>
              <input
                type="text"
                required
                value={formData.slug}
                onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                pattern="^[a-z0-9-]+$"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                placeholder="e.g., acme-corp"
              />
              <p className="mt-1 text-xs text-gray-500">Lowercase letters, numbers, and hyphens only</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Billing Email</label>
              <input
                type="email"
                value={formData.billing_email}
                onChange={(e) => setFormData(prev => ({ ...prev, billing_email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="billing@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
              <select
                value={formData.plan}
                onChange={(e) => handlePlanChange(e.target.value as TeamPlan)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="starter">Starter (5 users, 500 PoS)</option>
                <option value="growth">Growth (25 users, 2,500 PoS)</option>
                <option value="enterprise">Enterprise (100 users, 10,000 PoS)</option>
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.allow_super_admin_data_access}
                onChange={(e) => setFormData(prev => ({ ...prev, allow_super_admin_data_access: e.target.checked }))}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Allow super admin to view team data</span>
            </label>
          </div>
        </section>

        {/* Admin User */}
        <section className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold mb-4">Team Admin User</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Admin Name *</label>
              <input
                type="text"
                required
                value={formData.admin_name}
                onChange={(e) => setFormData(prev => ({ ...prev, admin_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="John Doe"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Admin Email *</label>
              <input
                type="email"
                required
                value={formData.admin_email}
                onChange={(e) => setFormData(prev => ({ ...prev, admin_email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="admin@example.com"
              />
            </div>
          </div>
          <p className="mt-3 text-sm text-gray-500">
            A random password will be generated for the admin user. You'll see it after team creation.
          </p>
        </section>

        {/* Modules */}
        <section className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold mb-4">Enabled Modules</h2>
          <div className="space-y-6">
            {Object.entries(modulesByCategory).map(([category, categoryModules]) => (
              <div key={category}>
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
                  {category}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {categoryModules.map((module) => (
                    <label
                      key={module.id}
                      className={`flex items-start gap-3 p-3 rounded-lg border ${
                        module.is_core 
                          ? 'bg-gray-50 border-gray-200 cursor-not-allowed' 
                          : 'bg-white border-gray-200 hover:border-blue-300 cursor-pointer'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.enabled_modules?.includes(module.id) || module.is_core}
                        onChange={() => handleModuleToggle(module.id, module.is_core)}
                        disabled={module.is_core}
                        className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                      />
                      <div>
                        <p className="font-medium text-gray-900">
                          {module.name}
                          {module.is_core && (
                            <span className="ml-2 text-xs text-gray-500">(Core - always enabled)</span>
                          )}
                        </p>
                        {module.description && (
                          <p className="text-sm text-gray-500">{module.description}</p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Submit */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/admin/teams')}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <Save size={20} />
            {isSubmitting ? 'Creating...' : 'Create Team'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateTeam;
